import {
  BACKEND_ERROR,
  useChangePaymentMethodMutation,
  useConfirmPayment,
  useRetryPaymentMutation,
} from "@stepapp/core";
import { PaymentStatus } from "@stepapp/core/apiTypes";
import { captureSentryException } from "@stepapp/sentry";
import { Block, Button, spacing } from "@stepapp/ui";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { StyleSheet } from "react-native";
import { useErrorMessage } from "../../hooks";
import { AddCard } from "./AddCard";
import { PaymentMethods } from "./PaymentMethods";

type Props = {
  paymentMethodId?: string;
  paymentId?: string;
  selectPaymentMethod: (paymentMethodId: string) => void;
  refetchReservation?: () => void;
  setSheetVisible?: () => void;
  onRetryPaymentSuccess?: () => void;
  changePayment?: boolean;
  cycleDuration?: string;
  price?: number | null;
  startDate?: string | null;
  bundleName?: string;
  reservationId: string;
};

export const UpdatePayment = ({
  paymentMethodId = "",
  paymentId,
  selectPaymentMethod,
  refetchReservation,
  onRetryPaymentSuccess,
  setSheetVisible,
  cycleDuration,
  price,
  startDate,
  bundleName,
  changePayment = false,
  reservationId,
}: Props) => {
  const { t } = useTranslation();
  const [newCard, setNewCard] = useState(false);
  const [loading, setLoading] = useState(false);

  const errorMessage = useErrorMessage();
  const stripeConfirmPayment = useConfirmPayment();
  const [retryPayment] = useRetryPaymentMutation();
  const [changePaymentMethod] = useChangePaymentMethodMutation();

  const handleRetryPayment = useCallback(async () => {
    setLoading(true);

    const { data: retryPaymentData, errors } = await retryPayment({
      variables: {
        paymentId: paymentId || null,
        stripePaymentMethodId: paymentMethodId,
        reservationId,
      },
    });

    if (
      retryPaymentData?.retryPayment.status === PaymentStatus.RequiresAction ||
      (errors && errors[0]?.extensions?.type === BACKEND_ERROR.PaymentRequiresAction)
    ) {
      const clientSecret =
        retryPaymentData?.retryPayment?.stripePaymentNextStep?.clientSecret ||
        // @ts-expect-error
        errors[0]?.extensions?.paymentNextAction?.clientSecret;

      const isConfirmed = await stripeConfirmPayment(
        clientSecret,
        paymentId || "",
        captureSentryException,
      );

      if (!isConfirmed) {
        setLoading(false);
        return alert(t("alerts.paymentFailed"));
      }
    }

    setLoading(false);
    onRetryPaymentSuccess?.();
  }, [
    retryPayment,
    stripeConfirmPayment,
    paymentId,
    paymentMethodId,
    reservationId,
    t,
    onRetryPaymentSuccess,
  ]);

  const submitNewPaymentMethod = useCallback(async () => {
    setLoading(true);
    const changePayment = await changePaymentMethod({
      variables: {
        paymentId: paymentId || "",
        paymentMethodId,
        reservationId,
      },
    });
    setLoading(false);
    if (changePayment?.errors) {
      const msg =
        errorMessage(changePayment?.errors?.[0]?.message || "") ??
        changePayment?.errors?.[0]?.message;
      return alert(msg);
    }
    refetchReservation?.();
    setSheetVisible?.();
  }, [
    changePaymentMethod,
    paymentId,
    paymentMethodId,
    reservationId,
    setSheetVisible,
    refetchReservation,
    errorMessage,
  ]);

  const onCompleteAddNewCard = useCallback(
    (id: string) => {
      selectPaymentMethod(id);
      setNewCard(false);
    },
    [selectPaymentMethod],
  );

  const handleSelectPayment = useCallback(() => {
    setNewCard(false);
  }, []);

  return (
    <>
      {newCard ? (
        <Block marginTop={3}>
          <AddCard loading={loading} setLoading={setLoading} onComplete={onCompleteAddNewCard} />
          <Block marginVertical={1}>
            <Button label={t("common.goBack")} onPress={handleSelectPayment} type="secondary" />
          </Block>
        </Block>
      ) : (
        <>
          <Block>
            <PaymentMethods
              paymentMethodId={paymentMethodId}
              selectPaymentMethod={selectPaymentMethod}
              setLoading={setLoading}
              cycleDuration={cycleDuration}
              price={price}
              startDate={startDate}
              bundleName={bundleName}
            />

            <Block row marginTop={1}>
              <Button
                containerStyle={styles.addCardButton}
                disabled={loading}
                label={t("pages.chooseCard.newCard")}
                onPress={() => setNewCard(true)}
                type="secondary"
                variant="small"
              />
            </Block>
          </Block>

          <Block flex={1} justifyEnd>
            <Button
              disabled={loading || !paymentMethodId}
              label={changePayment ? t("common.save") : t("screens.calendar.settlePayment")}
              loading={loading}
              onPress={changePayment ? submitNewPaymentMethod : handleRetryPayment}
              type="primary"
              variant="default"
              style={styles.saveButton}
            />
          </Block>
        </>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  addCardButton: {
    paddingHorizontal: spacing.unit * 4,
  },
  confirmBtn: {
    marginTop: spacing.unit * 3,
  },
  saveButton: {
    marginTop: spacing.unit * 3,
    marginBottom: spacing.unit * 2,
  },
});
